/**
 * V13.19 — Page admin Import Résultats Jeux du Pacifique / Mini-Jeux.
 *
 * Workflow 4 étapes :
 *   1. Upload (édition auto-détectée depuis le fichier)
 *   2. Preview (stats + tableau des décisions par ligne)
 *   3. Commit (application + création)
 *   4. Report + rollback si nécessaire
 *
 * Format Excel attendu : voir /api/games-import/upload (colonnes competition,
 * edition, sport, sexe, epreuve, date_resultat, participant_nom,
 * participant_type, delegation, rang, medaille, performance, source_url).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Play, Trophy, CheckCircle2, XCircle, Loader2, Undo2,
  Info, FileSpreadsheet, ShieldCheck, Users, Building2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { showError } from "@/lib/errors";

const DECISION_COLOR = {
  attach: "bg-emerald-600",
  create: "bg-[hsl(var(--aito-lagoon))]",
  create_team_result: "bg-slate-500",
  create_foreign_participant: "bg-indigo-500",
  review: "bg-amber-500",
  skip: "bg-neutral-400",
};

const DECISION_LABEL = {
  attach: "Rattacher",
  create: "Nouvelle fiche",
  create_team_result: "Résultat équipe",
  create_foreign_participant: "Participant étranger",
  review: "À vérifier",
  skip: "Déjà présent",
};

function StatTile({ icon: Icon, label, value, tone = "lagoon" }) {
  return (
    <Card className={`p-3 flex items-start gap-2 border-l-4 border-[hsl(var(--aito-${tone}))]`}>
      <Icon className={`h-4 w-4 mt-0.5 text-[hsl(var(--aito-${tone}))]`} />
      <div>
        <div className="text-2xl font-bold font-display leading-none">{value ?? 0}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-1">{label}</div>
      </div>
    </Card>
  );
}

function StatChip({ label, value, testid }) {
  const isZero = !value;
  return (
    <div
      className={`rounded-md border px-2 py-1.5 text-center ${isZero ? "border-slate-200 bg-slate-50 text-slate-500" : "border-red-300 bg-red-50 text-red-800"}`}
      data-testid={testid}
    >
      <div className="text-lg font-bold font-display leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}


export default function AdminGamesImportPage() {
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [batch, setBatch] = useState(null);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState("all");
  const [batches, setBatches] = useState([]);
  // V13.20 — Décisions locales par ligne (row_index → { action, athlete_id? })
  // Sauvegardées côté serveur via POST /decisions avant chaque commit ou changement d'onglet.
  const [decisions, setDecisions] = useState({});
  const [savingDecisions, setSavingDecisions] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/games-import/batches?limit=20");
      setBatches(data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setBatch(null); setPreview(null); setReport(null);
    setDecisions({});
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/games-import/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      toast.success(`Fichier chargé : ${data.total_rows} lignes.`);
      setStep(2);
      await runPreview(data.batch_id);
      await loadBatches();
    } catch (e) { showError(e, { prefix: "Upload :" }); }
    finally { setUploading(false); }
  };

  const runPreview = async (bid) => {
    setPreviewing(true);
    try {
      const { data } = await api.get(`/games-import/batches/${bid}/preview`);
      setPreview(data);
      // Pré-remplir les décisions locales avec les décisions déjà persistées
      const existing = {};
      for (const l of data.lines || []) {
        if (l.decision && l.decision !== "review") {
          // décisions non-review sont "figées" par le preview (attach auto / create / skip / team / foreign)
          existing[l.row_index] = { action: l.decision, athlete_id: l.athlete_id };
        }
      }
      setDecisions(existing);
    } catch (e) { showError(e, { prefix: "Preview :" }); }
    finally { setPreviewing(false); }
  };

  const saveDecisions = async (bid) => {
    if (!bid) return;
    setSavingDecisions(true);
    try {
      // On ne persiste QUE les décisions différentes de la décision auto du preview
      // (celles saisies par l'admin sur les lignes "review" typiquement).
      const payload = {};
      for (const l of preview?.lines || []) {
        const d = decisions[l.row_index];
        if (!d) continue;
        // On ne stocke que si l'admin a tranché quelque chose de non trivial
        // pour éviter d'écraser l'auto-matching.
        if (l.decision === "review" || d.action !== l.decision) {
          payload[String(l.row_index)] = d;
        }
      }
      if (Object.keys(payload).length > 0) {
        await api.post(`/games-import/batches/${bid}/decisions`, { decisions: payload });
      }
    } catch (e) {
      showError(e, { prefix: "Sauvegarde décisions :" });
    } finally {
      setSavingDecisions(false);
    }
  };

  const setLineDecision = (rowIndex, action, athleteId) => {
    setDecisions((prev) => ({
      ...prev,
      [rowIndex]: { action, ...(athleteId ? { athlete_id: athleteId } : {}) },
    }));
  };

  const commit = async () => {
    if (!batch) return;
    // V13.20 — Alerter si des lignes "review" n'ont pas été tranchées : elles
    // seront ignorées côté serveur (compté dans review_pending). L'admin peut
    // décider de committer quand même ou de revenir en arrière.
    const reviewLines = (preview?.lines || []).filter((l) => l.decision === "review");
    const decidedReview = reviewLines.filter((l) => decisions[l.row_index]);
    const undecidedCount = reviewLines.length - decidedReview.length;
    let msg = "Confirmer l'import ? Les fiches manquantes seront créées automatiquement.";
    if (undecidedCount > 0) {
      msg = `⚠ ${undecidedCount} ligne(s) "À vérifier" sans décision. Elles seront IGNORÉES à l'import (aucun résultat créé). Continuer quand même ?`;
    }
    if (!window.confirm(msg)) return;
    setCommitting(true);
    try {
      // Persister les décisions AVANT le commit (idempotent)
      await saveDecisions(batch.batch_id);
      const { data } = await api.post(`/games-import/batches/${batch.batch_id}/commit`);
      setReport(data);
      setStep(3);
      const created = data.report?.athletes_created ?? 0;
      const reused = data.report?.athletes_reused_intra_batch ?? 0;
      const suffix = reused > 0 ? ` · ${reused} multi-résultat(s) fusionné(s)` : "";
      toast.success(`Import terminé : ${data.report?.results_created ?? 0} résultats · ${created} nouveaux athlètes${suffix}.`);
      await loadBatches();
    } catch (e) { showError(e, { prefix: "Commit :" }); }
    finally { setCommitting(false); }
  };

  const rollback = async (bid) => {
    if (!window.confirm("Annuler cet import ? Tous les résultats seront supprimés. Les fiches athlètes non revendiquées seront supprimées définitivement (slug libéré pour ré-import) ; celles revendiquées entre-temps seront désactivées mais préservées.")) return;
    try {
      const { data } = await api.post(`/games-import/batches/${bid}/rollback`);
      const r = data.removed || {};
      toast.success(`Rollback effectué : ${r.results ?? 0} résultats · ${r.athletes_hard_deleted ?? 0} fiches supprimées · ${r.athletes_soft_deleted ?? 0} fiches désactivées.`);
      await loadBatches();
      if (batch?.batch_id === bid) { setBatch(null); setPreview(null); setReport(null); setStep(1); setDecisions({}); }
    } catch (e) { showError(e, { prefix: "Rollback :" }); }
  };

  // V13.28 — Purge totale : rollback + delete du batch en un clic.
  // Cible les batches "committed" (fichier importé mais qui ne devrait pas
  // rester) OU les batches "failed" laissés en état incohérent.
  const purge = async (bid) => {
    if (!window.confirm("SUPPRESSION DÉFINITIVE : les résultats, participations, événements et fiches athlètes non revendiquées créés par cet import seront effacés, puis le batch sera supprimé de la liste. Les fiches revendiquées entre-temps par un athlète seront désactivées mais préservées pour audit. Cette action est irréversible.")) return;
    try {
      const { data } = await api.post(`/games-import/batches/${bid}/purge`);
      const c = data.cleanup || {};
      toast.success(`Purge terminée : ${c.results_deleted ?? 0} résultats · ${c.athletes_hard_deleted ?? 0} fiches supprimées · ${c.athletes_soft_deleted ?? 0} fiches désactivées.`);
      await loadBatches();
      if (batch?.batch_id === bid) { setBatch(null); setPreview(null); setReport(null); setStep(1); setDecisions({}); }
    } catch (e) { showError(e, { prefix: "Purge :" }); }
  };

  // V13.20 — Suppression d'un batch NON committé (previewed/failed/rolled_back)
  // V13.28 — Nettoie aussi les artefacts orphelins éventuels (fiches créées
  // par un batch qui a crashé à mi-commit).
  const deleteBatch = async (bid) => {
    if (!window.confirm("Supprimer ce brouillon d'import ? Toute donnée éventuellement créée par un commit partiel sera aussi nettoyée.")) return;
    try {
      const { data } = await api.delete(`/games-import/batches/${bid}`);
      const c = data.cleanup || {};
      const extra = (c.athletes_hard_deleted || c.athletes_soft_deleted || c.results_deleted)
        ? ` · Nettoyage : ${c.athletes_hard_deleted ?? 0} fiches, ${c.results_deleted ?? 0} résultats`
        : "";
      toast.success("Batch supprimé." + extra);
      await loadBatches();
      if (batch?.batch_id === bid) { setBatch(null); setPreview(null); setReport(null); setStep(1); setDecisions({}); }
    } catch (e) { showError(e, { prefix: "Suppression :" }); }
  };

  // V13.29 — Nettoyage de masse : purge des fiches/résultats/events dont le
  // batch source a disparu (typiquement suite à un DELETE d'un batch fait
  // avec l'ancien code qui ne nettoyait pas les artefacts).
  const [orphans, setOrphans] = useState(null);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const loadOrphans = async () => {
    setOrphansLoading(true);
    try {
      const { data } = await api.get("/games-import/orphans");
      setOrphans(data);
    } catch (e) { showError(e, { prefix: "Audit orphelins :" }); }
    finally { setOrphansLoading(false); }
  };
  const purgeOrphans = async () => {
    if (!orphans) return;
    const tot = Object.values(orphans.orphans || {}).reduce((a, b) => a + b, 0);
    if (!tot) { toast.info("Aucun orphelin à purger."); return; }
    if (!window.confirm(`SUPPRESSION DÉFINITIVE : ${tot} artefact${tot > 1 ? "s" : ""} orphelin${tot > 1 ? "s" : ""} détecté${tot > 1 ? "s" : ""} — fiches non revendiquées et résultats seront supprimés définitivement, fiches revendiquées entre-temps seront désactivées mais préservées. Continuer ?`)) return;
    try {
      const { data } = await api.post("/games-import/orphans/purge");
      const r = data.removed || {};
      toast.success(`Purge orphelins : ${r.athletes_hard_deleted ?? 0} fiches supprimées · ${r.athletes_soft_deleted ?? 0} désactivées · ${r.results_deleted ?? 0} résultats · ${r.events_deleted ?? 0} events.`);
      await loadOrphans();
      await loadBatches();
    } catch (e) { showError(e, { prefix: "Purge orphelins :" }); }
  };

  // V13.32 — Purge de masse des fiches soft-deleted qui bloquent les slugs
  // pour les futurs imports (index unique historique). Aucune fiche revendiquée
  // n'est jamais touchée (owner_user_id set → protégée).
  const [softDeleted, setSoftDeleted] = useState(null);
  const [sdLoading, setSdLoading] = useState(false);
  const loadSoftDeleted = async () => {
    setSdLoading(true);
    try {
      const { data } = await api.get("/games-import/purge-soft-deleted/preview");
      setSoftDeleted(data);
    } catch (e) { showError(e, { prefix: "Audit fiches supprimées :" }); }
    finally { setSdLoading(false); }
  };
  const purgeSoftDeleted = async () => {
    if (!softDeleted) return;
    const n = softDeleted.purgeable_count || 0;
    if (!n) { toast.info("Aucune fiche soft-deleted à purger."); return; }
    if (!window.confirm(`SUPPRESSION DÉFINITIVE : ${n} fiche${n > 1 ? "s" : ""} athlète actuellement dans la corbeille (sans propriétaire) sera${n > 1 ? "nt" : ""} supprimée${n > 1 ? "s" : ""} définitivement — cela libère les slugs pour les prochains imports. ${softDeleted.protected_claimed_soft_deleted ? `${softDeleted.protected_claimed_soft_deleted} fiche(s) revendiquée(s) en corbeille NE seront PAS touchées.` : ""} Continuer ?`)) return;
    try {
      const { data } = await api.post("/games-import/purge-soft-deleted");
      toast.success(`${data.athletes_hard_deleted ?? 0} fiches purgées · ${data.pacific_athletes_hard_deleted ?? 0} Pacific purgées · ${data.freed_slugs_total ?? 0} slugs libérés.`);
      await loadSoftDeleted();
      await loadBatches();
    } catch (e) { showError(e, { prefix: "Purge fiches supprimées :" }); }
  };

  // Filtre les lignes preview selon l'onglet actif
  const filteredLines = (preview?.lines || []).filter((l) => {
    if (tab === "all") return true;
    return l.decision === tab;
  }).slice(0, 100);

  const stats = preview?.stats || {};

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-games-import-page">
      <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Retour admin</Button></Link>

      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <Trophy className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
          Import résultats Jeux du Pacifique / Mini-Jeux
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Chargement d'un fichier Excel .xlsx (13 colonnes fixes). Le système détecte l'édition, matche les athlètes existants,
          et propose des créations ou rattachements sous ta validation.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 text-xs">
        {[
          { n: 1, l: "Upload" },
          { n: 2, l: "Preview & décisions" },
          { n: 3, l: "Import & rapport" },
        ].map((s, i, arr) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center font-semibold ${step >= s.n ? "bg-[hsl(var(--aito-lagoon))] text-black" : "bg-muted text-muted-foreground"}`}>{s.n}</div>
            <span className={step >= s.n ? "font-semibold" : "text-muted-foreground"}>{s.l}</span>
            {i < arr.length - 1 && <div className="h-px w-8 bg-border" />}
          </div>
        ))}
      </div>

      {/* STEP 1 — Upload */}
      {step === 1 && (
        <Card className="p-6 border-dashed border-2" data-testid="step-upload">
          <div className="text-center">
            <FileSpreadsheet className="h-10 w-10 mx-auto text-[hsl(var(--aito-lagoon))] mb-2" />
            <h2 className="font-display font-semibold text-lg">Charger un fichier Excel</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              Colonnes attendues : competition, edition, sport, sexe, epreuve, date_resultat, participant_nom,
              participant_type, delegation, rang, medaille, performance, source_url.
            </p>
            <label className="inline-block mt-4">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                disabled={uploading}
                className="hidden"
                data-testid="upload-input"
              />
              <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))] cursor-pointer text-sm font-medium ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Choisir un fichier .xlsx
              </span>
            </label>
          </div>
        </Card>
      )}

      {/* STEP 2 — Preview */}
      {step >= 2 && preview && (
        <Card className="p-4 md:p-5 space-y-4" data-testid="step-preview">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
                Prévisualisation — {preview.edition?.name}
              </h2>
              <p className="text-xs text-muted-foreground">
                {stats.total} lignes analysées · Aucune modification tant que tu ne cliques pas « Lancer l'import ».
              </p>
              {/* V13.22 — Multi-éditions : si le fichier couvre plusieurs éditions,
                  on liste les éditions détectées pour que l'admin vérifie que
                  chaque année du fichier a bien été reconnue. */}
              {Array.isArray(preview.editions) && preview.editions.length > 1 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="preview-multi-editions">
                  <span className="text-[11px] text-muted-foreground font-medium">Éditions détectées :</span>
                  {preview.editions
                    .slice()
                    .sort((a, b) => (a.year || 0) - (b.year || 0))
                    .map((e) => (
                      <span
                        key={e.id}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border border-[hsl(var(--aito-lagoon))]/20"
                      >
                        {e.name || `${e.type} ${e.year}`}
                      </span>
                    ))}
                </div>
              )}
            </div>
            {step === 2 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => runPreview(batch.batch_id)}
                  disabled={previewing}
                  data-testid="btn-refresh-preview"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${previewing ? "animate-spin" : ""}`} />
                  Recalculer
                </Button>
                <Button
                  onClick={commit}
                  disabled={committing || stats.total === 0}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="btn-commit"
                >
                  {committing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                  Lancer l'import
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <StatTile icon={FileSpreadsheet} label="Total" value={stats.total} tone="lagoon" />
            <StatTile icon={CheckCircle2} label="Déjà présents" value={stats.already_present} tone="lagoon" />
            <StatTile icon={ShieldCheck} label="Rattachements auto" value={stats.athletes_matched_auto} tone="gold" />
            <StatTile icon={AlertTriangle} label="À vérifier" value={stats.athletes_review} tone="energy" />
            <StatTile icon={Users} label="Nouvelles fiches" value={stats.athletes_new_tahiti} tone="lagoon" />
            <StatTile icon={Building2} label="Équipes" value={stats.team_results} tone="gold" />
            <StatTile icon={Users} label="Étrangers" value={stats.participants_foreign} tone="lagoon" />
          </div>

          {/* Filtres */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="all" data-testid="tab-all">Toutes ({stats.total})</TabsTrigger>
              {stats.athletes_matched_auto > 0 && <TabsTrigger value="attach" data-testid="tab-attach">Rattachements ({stats.athletes_matched_auto})</TabsTrigger>}
              {stats.athletes_review > 0 && <TabsTrigger value="review" data-testid="tab-review">À vérifier ({stats.athletes_review})</TabsTrigger>}
              {stats.athletes_new_tahiti > 0 && <TabsTrigger value="create" data-testid="tab-create">Nouvelles fiches ({stats.athletes_new_tahiti})</TabsTrigger>}
              {stats.team_results > 0 && <TabsTrigger value="create_team_result" data-testid="tab-team">Équipes ({stats.team_results})</TabsTrigger>}
              {stats.participants_foreign > 0 && <TabsTrigger value="create_foreign_participant" data-testid="tab-foreign">Étrangers ({stats.participants_foreign})</TabsTrigger>}
              {stats.already_present > 0 && <TabsTrigger value="skip" data-testid="tab-skip">Déjà présents ({stats.already_present})</TabsTrigger>}
            </TabsList>

            <TabsContent value={tab} className="mt-3">
              <Card className="p-0 overflow-hidden">
                <div className="max-h-[520px] overflow-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-muted/60 sticky top-0">
                      <tr>
                        <th className="text-left p-2">#</th>
                        <th className="text-left p-2">Année</th>
                        <th className="text-left p-2">Participant</th>
                        <th className="text-left p-2">Délégation</th>
                        <th className="text-left p-2">Sport / Épreuve</th>
                        <th className="text-left p-2">Rang / Médaille</th>
                        <th className="text-left p-2">Décision auto</th>
                        <th className="text-left p-2">Décision admin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLines.map((l) => {
                        const dec = decisions[l.row_index];
                        const isReview = l.decision === "review";
                        const finalAction = dec?.action || (isReview ? null : l.decision);
                        return (
                        <tr key={l.row_index} className={`border-t hover:bg-muted/30 ${isReview && !dec ? "bg-amber-50" : ""}`} data-testid={`preview-row-${l.row_index}`}>
                          <td className="p-2 text-muted-foreground align-top">{l.row_index + 1}</td>
                          <td className="p-2 align-top">
                            {l.edition_year ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                {l.edition_year}
                              </span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-2 font-medium truncate max-w-[180px] align-top">{l.participant_nom}</td>
                          <td className="p-2 align-top">{l.delegation}</td>
                          <td className="p-2 truncate max-w-[180px] align-top">{l.sport} · {l.epreuve}</td>
                          <td className="p-2 align-top">
                            {l.rang && <span>#{l.rang}</span>}
                            {l.medaille && <Badge className="ml-1 text-[10px] bg-[hsl(var(--aito-gold))] text-black">{l.medaille}</Badge>}
                          </td>
                          <td className="p-2 align-top">
                            <Badge className={`${DECISION_COLOR[l.decision] || "bg-neutral-400"} text-white text-[10px]`}>
                              {DECISION_LABEL[l.decision] || l.decision}
                            </Badge>
                            {l.top_score > 0 && <span className="ml-1 text-[10px] text-muted-foreground">({(l.top_score * 100).toFixed(0)}%)</span>}
                          </td>
                          <td className="p-2 align-top min-w-[240px]">
                            {isReview ? (
                              <div className="space-y-1.5">
                                {(l.matches || []).slice(0, 3).map((m) => (
                                  <button
                                    key={m.athlete_id}
                                    type="button"
                                    onClick={() => setLineDecision(l.row_index, "attach", m.athlete_id)}
                                    className={`w-full text-left px-2 py-1 rounded border text-[11px] transition ${dec?.action === "attach" && dec?.athlete_id === m.athlete_id ? "bg-emerald-100 border-emerald-500 font-semibold" : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50"}`}
                                    data-testid={`decision-attach-${l.row_index}-${m.athlete_id}`}
                                  >
                                    <span className="inline-block mr-1">→</span>
                                    Rattacher à <span className="font-semibold">{m.full_name}</span>
                                    <span className="ml-1 text-muted-foreground">({(m.score * 100).toFixed(0)}%)</span>
                                  </button>
                                ))}
                                <div className="flex gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setLineDecision(l.row_index, "create")}
                                    className={`flex-1 px-2 py-1 rounded border text-[11px] transition ${dec?.action === "create" ? "bg-[hsl(var(--aito-lagoon))] text-white border-[hsl(var(--aito-lagoon))] font-semibold" : "border-slate-200 hover:border-[hsl(var(--aito-lagoon))]"}`}
                                    data-testid={`decision-create-${l.row_index}`}
                                  >
                                    + Nouvelle fiche
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setLineDecision(l.row_index, "skip")}
                                    className={`px-2 py-1 rounded border text-[11px] transition ${dec?.action === "skip" ? "bg-slate-500 text-white border-slate-500 font-semibold" : "border-slate-200 hover:border-slate-500"}`}
                                    data-testid={`decision-skip-${l.row_index}`}
                                    title="Ne rien créer pour cette ligne"
                                  >
                                    Ignorer
                                  </button>
                                </div>
                                {!dec && (
                                  <div className="text-[10px] text-amber-700 italic">
                                    Sans décision, cette ligne sera ignorée à l'import.
                                  </div>
                                )}
                                {dec && finalAction && (
                                  <div className="text-[10px] text-emerald-700 font-semibold">
                                    ✓ Décision : {DECISION_LABEL[finalAction] || finalAction}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-[11px] text-muted-foreground italic">
                                {l.decision === "skip" ? "Aucune action nécessaire" : "Traitée automatiquement"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
                {(preview.lines || []).length > 100 && (
                  <div className="text-center text-xs text-muted-foreground py-2 border-t bg-muted/30">
                    Affichage limité aux 100 premières lignes de ce filtre — {(preview.lines || []).length - 100} suivantes seront traitées à l'import.
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
          {step === 2 && stats.athletes_review > 0 && (
            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              <div className="text-xs text-muted-foreground">
                {(() => {
                  const reviewLines = (preview?.lines || []).filter((l) => l.decision === "review");
                  const decided = reviewLines.filter((l) => decisions[l.row_index]).length;
                  return `Décisions "À vérifier" : ${decided} / ${reviewLines.length} tranchées`;
                })()}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveDecisions(batch.batch_id)}
                disabled={savingDecisions}
                data-testid="btn-save-decisions"
              >
                {savingDecisions ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                Sauvegarder les décisions
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* STEP 3 — Rapport */}
      {step === 3 && report && (
        <Card className="p-6 border-l-4 border-emerald-500" data-testid="step-report">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 shrink-0" />
            <div className="flex-1">
              <h2 className="font-display font-semibold text-lg">Import terminé</h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                <StatTile icon={Trophy} label="Résultats créés" value={report.report?.results_created} tone="gold" />
                <StatTile icon={Users} label="Nouvelles fiches" value={report.report?.athletes_created} tone="lagoon" />
                <StatTile icon={Users} label="Multi-résultats fusionnés" value={report.report?.athletes_reused_intra_batch} tone="gold" />
                <StatTile icon={Building2} label="Étrangers" value={report.report?.participants_created} tone="lagoon" />
                <StatTile icon={ShieldCheck} label="Épreuves créées" value={report.report?.events_created} tone="lagoon" />
              </div>
              {(report.report?.review_pending || 0) > 0 && (
                <div className="mt-3 p-2 rounded border border-amber-300 bg-amber-50 text-xs" data-testid="report-review-pending">
                  <AlertTriangle className="inline h-3 w-3 mr-1 text-amber-600" />
                  <strong>{report.report.review_pending} ligne(s) « À vérifier » ignorée(s)</strong> — aucune décision manuelle n'a été prise. Relance un preview pour trancher, ou lance un nouvel import ciblé.
                </div>
              )}
              {(report.report?.errors?.length || 0) > 0 && (
                <div className="mt-3 p-2 rounded border border-amber-300 bg-amber-50 text-xs">
                  <AlertTriangle className="inline h-3 w-3 mr-1 text-amber-600" />
                  {report.report.errors.length} erreur(s) — voir logs.
                </div>
              )}
              <div className="mt-3 p-2 rounded border border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/5 text-xs">
                <Info className="inline h-3 w-3 mr-1 text-[hsl(var(--aito-lagoon))]" />
                Doublons potentiels ? Utilise l'outil{" "}
                <Link to="/admin/athletes/duplicates" className="font-semibold text-[hsl(var(--aito-lagoon))] hover:underline" data-testid="link-duplicates">
                  Détection de doublons
                </Link>
                {" "}ou{" "}
                <Link to="/admin/athletes/merge" className="font-semibold text-[hsl(var(--aito-lagoon))] hover:underline" data-testid="link-merge">
                  Fusion d'athlètes
                </Link>
                {" "}pour consolider les fiches homonymes.
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button onClick={() => { setStep(1); setBatch(null); setPreview(null); setReport(null); setDecisions({}); }} data-testid="btn-new-import">
                  <Upload className="h-4 w-4 mr-1.5" />Nouvel import
                </Button>
                <Button variant="outline" onClick={() => rollback(batch.batch_id)} className="text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="btn-rollback">
                  <Undo2 className="h-4 w-4 mr-1.5" />Annuler cet import
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* V13.29 — Nettoyage de masse des orphelins */}
      <Card className="p-4" data-testid="orphans-cleaner">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-600" />
              Nettoyage des orphelins d'import
            </h3>
            <p className="text-xs text-muted-foreground mt-1 leading-snug max-w-2xl">
              Recense les fiches, résultats et événements dont le batch d'import a été supprimé
              (typiquement suite à une suppression avec l'ancien code, ou après un crash à mi-commit).
              Ces artefacts polluent l'annuaire public et empêchent les slugs d'être libérés pour un ré-import propre.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={loadOrphans} disabled={orphansLoading} data-testid="btn-orphans-audit">
              {orphansLoading ? "…" : "Auditer"}
            </Button>
            {orphans && Object.values(orphans.orphans || {}).some((v) => v > 0) && (
              <Button size="sm" onClick={purgeOrphans} className="bg-red-600 text-white hover:bg-red-700" data-testid="btn-orphans-purge">
                Purger tout
              </Button>
            )}
          </div>
        </div>
        {orphans && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs">
            <StatChip label="Fiches athlètes" value={orphans.orphans.athletes} testid="orphans-athletes" />
            <StatChip label="Fiches Pacific" value={orphans.orphans.pacific_athletes} testid="orphans-pa" />
            <StatChip label="Résultats" value={orphans.orphans.games_results} testid="orphans-results" />
            <StatChip label="Participations" value={orphans.orphans.games_participants} testid="orphans-participants" />
            <StatChip label="Épreuves" value={orphans.orphans.games_events} testid="orphans-events" />
          </div>
        )}
        {orphans?.sample_athletes?.length > 0 && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Voir un échantillon ({orphans.sample_athletes.length} fiche{orphans.sample_athletes.length > 1 ? "s" : ""})
            </summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc text-muted-foreground">
              {orphans.sample_athletes.map((a) => (
                <li key={a.id}>
                  <span className="font-medium text-foreground">{a.full_name}</span>
                  <span className="ml-1 text-[10px]">(/{a.slug})</span>
                  {a.owner_user_id && <span className="ml-1 text-emerald-700 text-[10px]">— revendiquée (sera préservée)</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </Card>

      {/* V13.32 — Purge des fiches soft-deleted (corbeille définitive) */}
      <Card className="p-4" data-testid="soft-deleted-cleaner">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2">
              <XCircle className="h-4 w-4 text-slate-700" />
              Corbeille — Fiches supprimées à purger
            </h3>
            <p className="text-xs text-muted-foreground mt-1 leading-snug max-w-2xl">
              Vide définitivement la corbeille des fiches athlètes importées ou d'origine legacy
              qui ont été « supprimées doucement » (soft-delete). Ces fiches occupaient historiquement
              les slugs et pouvaient bloquer la création de nouvelles fiches lors des imports.
              <br />
              <span className="italic">Les fiches revendiquées par un athlète (propriétaire renseigné) ne sont jamais touchées, même en corbeille.</span>
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={loadSoftDeleted} disabled={sdLoading} data-testid="btn-soft-audit">
              {sdLoading ? "…" : "Auditer la corbeille"}
            </Button>
            {softDeleted && softDeleted.purgeable_count > 0 && (
              <Button size="sm" onClick={purgeSoftDeleted} className="bg-red-600 text-white hover:bg-red-700" data-testid="btn-soft-purge">
                Vider ({softDeleted.purgeable_count})
              </Button>
            )}
          </div>
        </div>
        {softDeleted && (
          <div className="mt-3 space-y-2 text-xs">
            <div className="flex flex-wrap gap-2">
              <StatChip label="À purger" value={softDeleted.purgeable_count} testid="soft-count" />
              {Object.entries(softDeleted.by_origin || {}).map(([k, v]) => (
                <StatChip key={k} label={k} value={v} testid={`soft-origin-${k}`} />
              ))}
              {softDeleted.protected_claimed_soft_deleted > 0 && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-1.5 text-center">
                  <div className="text-lg font-bold font-display leading-none">{softDeleted.protected_claimed_soft_deleted}</div>
                  <div className="text-[10px] uppercase tracking-wide mt-0.5">Protégées (revendiquées)</div>
                </div>
              )}
            </div>
            {softDeleted.sample?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Aperçu ({softDeleted.sample.length} fiche{softDeleted.sample.length > 1 ? "s" : ""})
                </summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc text-muted-foreground">
                  {softDeleted.sample.map((a) => (
                    <li key={a.id}>
                      <span className="font-medium text-foreground">{a.full_name}</span>
                      <span className="ml-1 text-[10px]">(/{a.slug})</span>
                      <span className="ml-1 text-[10px]">— {a.delete_reason || "raison inconnue"}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* Historique des imports */}
      <Card className="p-4" data-testid="batches-history">
        <h3 className="font-display font-semibold text-sm mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          Historique des imports ({batches.length})
        </h3>
        {batches.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun import pour l'instant.</p>
        ) : (
          <div className="space-y-1.5 text-xs">
            {batches.map((b) => {
              const isCommitted = b.status === "committed";
              const isRollable = isCommitted;
              const isDeletable = ["previewed", "pending_preview", "failed", "rolled_back"].includes(b.status);
              const badgeCls = {
                committed: "bg-emerald-100 text-emerald-800 border-emerald-300",
                previewed: "bg-amber-100 text-amber-800 border-amber-300",
                pending_preview: "bg-slate-100 text-slate-700 border-slate-300",
                rolled_back: "bg-orange-100 text-orange-800 border-orange-300",
                failed: "bg-red-100 text-red-800 border-red-300",
              }[b.status] || "";
              return (
                <div key={b.id} className="flex items-center gap-2 p-2 rounded border bg-background hover:bg-muted/30" data-testid={`batch-${b.id}`}>
                  <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-mono text-[10px] text-muted-foreground">{b.id.slice(0, 8)}</span>
                  <span className="flex-1 truncate">{b.filename}</span>
                  <span className="text-muted-foreground shrink-0">{b.total_rows} lignes</span>
                  <Badge variant="outline" className={`text-[10px] ${badgeCls}`}>{b.status}</Badge>
                  {isRollable && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rollback(b.id)}
                      className="h-7 text-orange-700 border-orange-300 hover:bg-orange-50"
                      data-testid={`btn-rollback-${b.id}`}
                    >
                      <Undo2 className="h-3 w-3 mr-1" />Annuler
                    </Button>
                  )}
                  {/* V13.28 — Purge totale : disponible sur tous les statuts.
                      Rollback + delete du batch en un seul clic, avec cleanup
                      exhaustif des artefacts (fiches, résultats, events). */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => purge(b.id)}
                    className="h-7 text-red-800 border-red-400 hover:bg-red-100 bg-red-50/50"
                    data-testid={`btn-purge-${b.id}`}
                    title="Rollback + suppression totale du batch et de ses artefacts"
                  >
                    <XCircle className="h-3 w-3 mr-1" />Purger
                  </Button>
                  {isDeletable && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteBatch(b.id)}
                      className="h-7 text-red-700 border-red-300 hover:bg-red-50"
                      data-testid={`btn-delete-${b.id}`}
                    >
                      <XCircle className="h-3 w-3 mr-1" />Supprimer
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-[10px] text-muted-foreground italic">
          <strong>« Annuler »</strong> : sur un batch committé — supprime résultats, hard-delete fiches non revendiquées (slug libéré), soft-delete fiches revendiquées.
          <br />
          <strong>« Supprimer »</strong> : sur un brouillon non committé — efface le batch et nettoie tout artefact orphelin éventuel.
          <br />
          <strong>« Purger »</strong> : rollback + suppression en un clic sur n'importe quel statut. Irréversible.
        </p>
      </Card>
    </div>
  );
}
